import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShieldCheck, Target, ScrollText, ClipboardCheck, MessageSquareWarning,
  Plus, Loader2, X, Edit3, Trash2, Check, AlertTriangle, FlaskConical,
  Calendar, ChevronRight, EyeOff, Lock, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";

const TIER_COLOR = { critical: "#DC2626", high: "#EA580C", medium: "#F59E0B", low: "#22C55E" };
const SEV_COLOR = { critical: "#DC2626", high: "#EA580C", medium: "#F59E0B", low: "#94A3B8" };
const STATUS_COLOR = {
  open: "#EF4444", in_progress: "#F59E0B", closed: "#22C55E", accepted_risk: "#94A3B8",
  mitigated: "#22C55E", accepted: "#94A3B8", transferred: "#94A3B8",
  received: "#F59E0B", triaged: "#3B82F6", investigating: "#A855F7",
  planning: "#94A3B8", fieldwork: "#3B82F6", draft: "#A855F7", reporting: "#F59E0B",
};

const TABS = [
  { id: "dashboard", label: "Dashboard", icon: ShieldCheck },
  { id: "risks", label: "Risk Register", icon: Target },
  { id: "controls", label: "Controls", icon: ScrollText },
  { id: "audits", label: "Audits", icon: ClipboardCheck },
  { id: "ethics", label: "Ethics", icon: MessageSquareWarning },
];

export default function Grc() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "dashboard";
  const setTab = (id) => setParams({ tab: id });
  const [taxonomies, setTaxonomies] = useState(null);

  useEffect(() => { api.get("/grc/taxonomies").then((r) => setTaxonomies(r.data)); }, []);

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="grc-page">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <ShieldAlert className="w-3.5 h-3.5" style={{ color: "var(--gold-400)" }} strokeWidth={1.6} />
          <span className="label-eyebrow">// Governance, Risk & Compliance</span>
        </div>
        <h1 className="font-display text-3xl text-white tracking-tight">GRC Suite</h1>
        <p className="text-slate-400 text-sm mt-1 max-w-2xl">
          Risk register with 5×5 heatmap, controls library mapped to risks, audit-readiness with findings tracking,
          and a confidential ethics &amp; whistleblower channel.
        </p>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-white/5" data-testid="grc-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`grc-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 flex items-center gap-2 font-mono text-[11px] tracking-widest transition-all border-b-2 -mb-px ${
              tab === t.id ? "border-[#D4AF37] text-white" : "border-transparent text-slate-400 hover:text-white"
            }`}
          >
            <t.icon className="w-3 h-3" strokeWidth={1.6} />
            {t.label.toUpperCase()}
          </button>
        ))}
      </div>

      {tab === "dashboard" && <Dashboard onTab={setTab} />}
      {tab === "risks" && <RisksPane taxonomies={taxonomies} />}
      {tab === "controls" && <ControlsPane taxonomies={taxonomies} />}
      {tab === "audits" && <AuditsPane taxonomies={taxonomies} />}
      {tab === "ethics" && <EthicsPane taxonomies={taxonomies} />}
    </div>
  );
}

/* ─────────────────────────── DASHBOARD ─────────────────────────── */
function Dashboard({ onTab }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/grc/dashboard").then((r) => setD(r.data)); }, []);
  if (!d) return <Loading />;
  return (
    <div className="space-y-6" data-testid="grc-dashboard">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <BigStat label="Open Risks" value={d.risks.open} sub={`${d.risks.total} total`} accent="#D4AF37" onClick={() => onTab("risks")} />
        <BigStat label="Control Coverage" value={`${d.controls.coverage_percent}%`} sub={`${d.controls.total} controls · ${d.controls.stale} stale`} accent="#3B82F6" onClick={() => onTab("controls")} />
        <BigStat label="Open Findings" value={d.audits.open_findings} sub={`${d.audits.critical_findings} critical`} accent="#EF4444" onClick={() => onTab("audits")} />
        {d.ethics && <BigStat label="Open Ethics Cases" value={d.ethics.open_cases} sub={`${d.ethics.total} total`} accent="#A855F7" onClick={() => onTab("ethics")} />}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="nexus-glass-panel p-5 lg:col-span-1">
          <div className="label-eyebrow mb-3">// Residual risk by tier</div>
          <div className="space-y-2">
            {["critical", "high", "medium", "low"].map((t) => {
              const v = d.risks.by_tier[t] || 0;
              const max = Math.max(1, ...Object.values(d.risks.by_tier));
              return (
                <div key={t} data-testid={`tier-${t}`}>
                  <div className="flex justify-between text-[11px] mb-1">
                    <span className="font-mono tracking-widest" style={{ color: TIER_COLOR[t] }}>{t.toUpperCase()}</span>
                    <span className="text-slate-300 font-mono">{v}</span>
                  </div>
                  <div className="h-1.5 bg-white/5 overflow-hidden">
                    <div className="h-full" style={{ width: `${(v / max) * 100}%`, background: TIER_COLOR[t] }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="nexus-glass-panel p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <span className="label-eyebrow">// Top 5 residual risks</span>
            <button onClick={() => onTab("risks")} className="font-mono text-[10px] tracking-widest text-slate-400 hover:text-[#D4AF37]">
              VIEW REGISTER →
            </button>
          </div>
          {d.risks.top.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">No risks logged yet.</div>
          ) : (
            <div className="space-y-1.5">
              {d.risks.top.map((r) => (
                <div key={r.id} className="flex items-center gap-3 p-2.5 nexus-card" data-testid={`top-risk-${r.id}`}>
                  <div className="w-9 h-9 flex items-center justify-center text-sm font-display font-bold shrink-0"
                       style={{ background: (TIER_COLOR[r.residual_tier] || "#94A3B8") + "1A",
                                border: `1px solid ${TIER_COLOR[r.residual_tier] || "#94A3B8"}40`,
                                color: TIER_COLOR[r.residual_tier] || "#94A3B8" }}>
                    {r.residual_score}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{r.title}</div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {r.category} · L{r.residual_likelihood ?? r.likelihood} × I{r.residual_impact ?? r.impact}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────── RISKS ─────────────────────────── */
function RisksPane({ taxonomies }) {
  const [rows, setRows] = useState([]);
  const [heatmap, setHeatmap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(null); // null | "new" | risk
  const [filterCat, setFilterCat] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const load = async () => {
    setLoading(true);
    const q = [];
    if (filterCat) q.push(`category=${filterCat}`);
    if (filterStatus) q.push(`status=${filterStatus}`);
    const [r, h] = await Promise.all([
      api.get(`/grc/risks${q.length ? "?" + q.join("&") : ""}`),
      api.get("/grc/risks/heatmap"),
    ]);
    setRows(r.data);
    setHeatmap(h.data);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterCat, filterStatus]);

  const del = async (id) => {
    if (!confirm("Delete this risk?")) return;
    await api.delete(`/grc/risks/${id}`);
    toast.success("Risk deleted");
    load();
  };

  if (loading) return <Loading />;

  return (
    <div className="space-y-5" data-testid="risks-pane">
      {/* Heatmap */}
      <div className="nexus-glass-panel p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="label-eyebrow">// Residual risk heatmap · 5×5</div>
            <div className="text-[11px] text-slate-500 mt-1">Likelihood (X) × Impact (Y) — excludes closed &amp; transferred</div>
          </div>
          <div className="font-mono text-[10px] text-slate-500 tracking-widest">{heatmap?.total || 0} ACTIVE</div>
        </div>
        <Heatmap data={heatmap} />
      </div>

      {/* Filters + Add */}
      <div className="flex flex-wrap items-center gap-2">
        <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)} data-testid="risk-filter-cat"
                className="bg-[#0A111F] border border-white/10 text-sm text-white px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none">
          <option value="">All categories</option>
          {taxonomies?.risk_categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="risk-filter-status"
                className="bg-[#0A111F] border border-white/10 text-sm text-white px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none">
          <option value="">All statuses</option>
          {taxonomies?.risk_statuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <div className="flex-1" />
        <button onClick={() => setShowForm("new")} data-testid="risk-add"
                className="text-[11px] font-mono tracking-widest px-4 py-2 flex items-center gap-2"
                style={{ background: "var(--gold-500)", color: "#050B14" }}>
          <Plus className="w-3 h-3" /> LOG RISK
        </button>
      </div>

      {/* List */}
      <div className="space-y-2">
        {rows.length === 0 ? (
          <div className="nexus-card p-8 text-center text-sm text-slate-400">No risks logged yet.</div>
        ) : rows.map((r) => (
          <div key={r.id} className="nexus-card p-4 flex items-start gap-4" data-testid={`risk-${r.id}`}>
            <div className="w-12 h-12 flex items-center justify-center text-base font-display font-bold shrink-0"
                 style={{ background: (TIER_COLOR[r.residual_tier] || "#94A3B8") + "1A",
                          border: `1px solid ${TIER_COLOR[r.residual_tier] || "#94A3B8"}50`,
                          color: TIER_COLOR[r.residual_tier] || "#94A3B8" }}>
              {r.residual_score}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h3 className="font-display text-base text-white">{r.title}</h3>
                <span className="font-mono text-[9px] tracking-widest px-1.5 py-0.5"
                      style={{ background: (TIER_COLOR[r.residual_tier] || "#94A3B8") + "20",
                               color: TIER_COLOR[r.residual_tier] || "#94A3B8" }}>
                  {r.residual_tier?.toUpperCase()}
                </span>
                <span className="font-mono text-[9px] tracking-widest text-slate-500">· {r.category}</span>
                <span className="font-mono text-[9px] tracking-widest" style={{ color: STATUS_COLOR[r.status] }}>
                  · {r.status?.toUpperCase()}
                </span>
              </div>
              {r.description && <p className="text-[12px] text-slate-400 leading-snug line-clamp-2">{r.description}</p>}
              <div className="flex gap-4 mt-2 text-[10px] font-mono tracking-widest text-slate-500">
                <span>INHERENT: L{r.likelihood}×I{r.impact}={r.inherent_score}</span>
                <span>RESIDUAL: L{r.residual_likelihood}×I{r.residual_impact}={r.residual_score}</span>
                {r.review_due_date && <span>REVIEW: {new Date(r.review_due_date).toLocaleDateString()}</span>}
              </div>
            </div>
            <div className="flex gap-1 shrink-0">
              <button onClick={() => setShowForm(r)} data-testid={`risk-edit-${r.id}`}
                      className="p-2 border border-white/10 text-slate-400 hover:border-[#D4AF37]/40 hover:text-[#D4AF37]">
                <Edit3 className="w-3 h-3" />
              </button>
              <button onClick={() => del(r.id)} data-testid={`risk-del-${r.id}`}
                      className="p-2 border border-white/10 text-slate-400 hover:border-red-500/40 hover:text-red-400">
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <AnimatePresence>
        {showForm && (
          <RiskForm initial={showForm === "new" ? null : showForm} taxonomies={taxonomies}
                    onClose={() => setShowForm(null)} onSaved={() => { setShowForm(null); load(); }} />
        )}
      </AnimatePresence>
    </div>
  );
}

function Heatmap({ data }) {
  if (!data) return null;
  const cellColor = (l, i) => {
    const s = l * i;
    return s >= 17 ? "#DC262633" : s >= 10 ? "#EA580C33" : s >= 5 ? "#F59E0B33" : "#22C55E33";
  };
  const cellBorder = (l, i) => {
    const s = l * i;
    return s >= 17 ? "#DC262680" : s >= 10 ? "#EA580C80" : s >= 5 ? "#F59E0B80" : "#22C55E80";
  };
  return (
    <div className="overflow-x-auto">
      <div className="inline-grid" style={{ gridTemplateColumns: "auto repeat(5, 56px)" }}>
        {/* corner */}
        <div className="font-mono text-[10px] text-slate-600 tracking-widest p-2 text-right">IMPACT ↑ / LIKELIHOOD →</div>
        {[1, 2, 3, 4, 5].map((l) => (
          <div key={`hx-${l}`} className="font-mono text-[10px] text-slate-500 text-center p-2">L{l}</div>
        ))}
        {[5, 4, 3, 2, 1].map((i) => (
          <React.Fragment key={`row-${i}`}>
            <div className="font-mono text-[10px] text-slate-500 text-right pr-2 self-center">I{i}</div>
            {[1, 2, 3, 4, 5].map((l) => {
              const count = data.grid[`${l}_${i}`] || 0;
              return (
                <div key={`${l}_${i}`} data-testid={`cell-${l}-${i}`}
                     className="aspect-square flex items-center justify-center text-xs font-mono"
                     style={{ background: cellColor(l, i), border: `1px solid ${cellBorder(l, i)}` }}
                     title={`L${l} × I${i} = ${l * i} · ${count} risk${count === 1 ? "" : "s"}`}>
                  {count > 0 ? <span className="text-white font-display text-base">{count}</span> : <span className="text-slate-600">·</span>}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function RiskForm({ initial, taxonomies, onClose, onSaved }) {
  const isEdit = !!initial;
  const [f, setF] = useState({
    title: initial?.title || "",
    description: initial?.description || "",
    category: initial?.category || "operational",
    status: initial?.status || "open",
    likelihood: initial?.likelihood || 3,
    impact: initial?.impact || 3,
    residual_likelihood: initial?.residual_likelihood || initial?.likelihood || 3,
    residual_impact: initial?.residual_impact || initial?.impact || 3,
    mitigation_plan: initial?.mitigation_plan || "",
    review_due_date: initial?.review_due_date || "",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!f.title.trim()) return toast.error("Title required");
    setBusy(true);
    try {
      if (isEdit) await api.put(`/grc/risks/${initial.id}`, f);
      else await api.post("/grc/risks", f);
      toast.success(isEdit ? "Risk updated" : "Risk logged");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return <Modal title={isEdit ? "Edit risk" : "Log a risk"} onClose={onClose} testid="risk-form">
    <Field label="Title">
      <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="risk-form-title"
             className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none" />
    </Field>
    <Field label="Description">
      <textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={3}
                className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none" />
    </Field>
    <div className="grid grid-cols-2 gap-3">
      <Field label="Category">
        <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}
                className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none">
          {taxonomies?.risk_categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
      </Field>
      <Field label="Status">
        <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}
                className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none">
          {taxonomies?.risk_statuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </Field>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <Field label="Inherent Likelihood (1-5)">
        <input type="number" min={1} max={5} value={f.likelihood} onChange={(e) => setF({ ...f, likelihood: +e.target.value })} data-testid="risk-form-likelihood"
               className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none" />
      </Field>
      <Field label="Inherent Impact (1-5)">
        <input type="number" min={1} max={5} value={f.impact} onChange={(e) => setF({ ...f, impact: +e.target.value })} data-testid="risk-form-impact"
               className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none" />
      </Field>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <Field label="Residual Likelihood (1-5)">
        <input type="number" min={1} max={5} value={f.residual_likelihood} onChange={(e) => setF({ ...f, residual_likelihood: +e.target.value })}
               className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none" />
      </Field>
      <Field label="Residual Impact (1-5)">
        <input type="number" min={1} max={5} value={f.residual_impact} onChange={(e) => setF({ ...f, residual_impact: +e.target.value })}
               className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none" />
      </Field>
    </div>
    <Field label="Mitigation plan">
      <textarea value={f.mitigation_plan} onChange={(e) => setF({ ...f, mitigation_plan: e.target.value })} rows={2}
                className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none" />
    </Field>
    <Field label="Review due date">
      <input type="date" value={f.review_due_date?.slice(0, 10) || ""} onChange={(e) => setF({ ...f, review_due_date: e.target.value })}
             className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none" />
    </Field>
    <SaveRow onSave={save} onClose={onClose} busy={busy} testid="risk-form-save" />
  </Modal>;
}

/* ─────────────────────────── CONTROLS ─────────────────────────── */
function ControlsPane({ taxonomies }) {
  const [rows, setRows] = useState([]);
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(null);
  const [testFor, setTestFor] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);

  const load = async () => {
    setLoading(true);
    const [c, r] = await Promise.all([api.get("/grc/controls"), api.get("/grc/risks")]);
    setRows(c.data);
    setRisks(r.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!confirm("Delete this control and its test history?")) return;
    await api.delete(`/grc/controls/${id}`);
    toast.success("Control deleted");
    load();
  };

  if (loading) return <Loading />;

  return (
    <div className="space-y-4" data-testid="controls-pane">
      <div className="flex items-center justify-between">
        <span className="label-eyebrow">// Controls library · {rows.length}</span>
        <button onClick={() => setShowForm("new")} data-testid="control-add"
                className="text-[11px] font-mono tracking-widest px-4 py-2 flex items-center gap-2"
                style={{ background: "var(--gold-500)", color: "#050B14" }}>
          <Plus className="w-3 h-3" /> ADD CONTROL
        </button>
      </div>
      {rows.length === 0 ? (
        <div className="nexus-card p-8 text-center text-sm text-slate-400">No controls yet.</div>
      ) : (
        <div className="space-y-2">
          {rows.map((c) => {
            const resColor = c.last_test_result === "effective" ? "#22C55E"
              : c.last_test_result === "partial" ? "#F59E0B"
              : c.last_test_result === "ineffective" ? "#EF4444" : "#94A3B8";
            return (
              <div key={c.id} className="nexus-card p-4" data-testid={`control-${c.id}`}>
                <div className="flex items-start gap-4">
                  <div className="w-9 h-9 flex items-center justify-center shrink-0"
                       style={{ background: "rgba(212,175,55,0.10)", border: "1px solid rgba(212,175,55,0.30)" }}>
                    <ScrollText className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.6} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="font-display text-base text-white">{c.title}</h3>
                      <span className="font-mono text-[9px] tracking-widest px-1.5 py-0.5 border border-[#D4AF37]/30 text-[#D4AF37]">{c.type?.toUpperCase()}</span>
                      <span className="font-mono text-[9px] tracking-widest text-slate-500">· {c.frequency?.toUpperCase()}</span>
                      {c.framework_ref && <span className="font-mono text-[9px] tracking-widest text-slate-400">· {c.framework_ref}</span>}
                    </div>
                    {c.description && <p className="text-[12px] text-slate-400 leading-snug line-clamp-2">{c.description}</p>}
                    <div className="flex gap-4 mt-2 text-[10px] font-mono tracking-widest text-slate-500 flex-wrap">
                      <span>RISKS: {(c.linked_risks || []).length}</span>
                      <span>TESTS: {c.test_count || 0}</span>
                      <span style={{ color: resColor }}>
                        LAST: {c.last_test_at ? new Date(c.last_test_at).toLocaleDateString() : "—"}
                        {c.last_test_result ? ` · ${c.last_test_result.toUpperCase()}` : ""}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => setTestFor(c)} data-testid={`control-test-${c.id}`}
                            className="p-2 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                            title="Record test run">
                      <FlaskConical className="w-3 h-3" />
                    </button>
                    <button onClick={() => setHistoryFor(c)} data-testid={`control-history-${c.id}`}
                            className="p-2 border border-white/10 text-slate-400 hover:border-[#D4AF37]/40 hover:text-[#D4AF37]"
                            title="Test history">
                      <Calendar className="w-3 h-3" />
                    </button>
                    <button onClick={() => setShowForm(c)} className="p-2 border border-white/10 text-slate-400 hover:border-[#D4AF37]/40 hover:text-[#D4AF37]">
                      <Edit3 className="w-3 h-3" />
                    </button>
                    <button onClick={() => del(c.id)} className="p-2 border border-white/10 text-slate-400 hover:border-red-500/40 hover:text-red-400">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AnimatePresence>
        {showForm && <ControlForm initial={showForm === "new" ? null : showForm} risks={risks} taxonomies={taxonomies}
                                  onClose={() => setShowForm(null)} onSaved={() => { setShowForm(null); load(); }} />}
        {testFor && <ControlTestForm control={testFor} onClose={() => setTestFor(null)} onSaved={() => { setTestFor(null); load(); }} />}
        {historyFor && <ControlHistory control={historyFor} onClose={() => setHistoryFor(null)} />}
      </AnimatePresence>
    </div>
  );
}

function ControlForm({ initial, risks, taxonomies, onClose, onSaved }) {
  const isEdit = !!initial;
  const [f, setF] = useState({
    title: initial?.title || "",
    description: initial?.description || "",
    type: initial?.type || "preventive",
    frequency: initial?.frequency || "quarterly",
    framework_ref: initial?.framework_ref || "",
    linked_risks: initial?.linked_risks || [],
  });
  const [busy, setBusy] = useState(false);
  const toggleRisk = (id) => setF((p) => ({
    ...p,
    linked_risks: p.linked_risks.includes(id) ? p.linked_risks.filter((x) => x !== id) : [...p.linked_risks, id]
  }));
  const save = async () => {
    if (!f.title.trim()) return toast.error("Title required");
    setBusy(true);
    try {
      if (isEdit) await api.put(`/grc/controls/${initial.id}`, f);
      else await api.post("/grc/controls", f);
      toast.success(isEdit ? "Control updated" : "Control added");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };
  return <Modal title={isEdit ? "Edit control" : "Add control"} onClose={onClose} testid="control-form">
    <Field label="Title">
      <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="control-form-title"
             className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none" />
    </Field>
    <Field label="Description"><textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })}
             className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none" /></Field>
    <div className="grid grid-cols-2 gap-3">
      <Field label="Type">
        <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}
                className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none">
          {taxonomies?.control_types.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </Field>
      <Field label="Frequency">
        <select value={f.frequency} onChange={(e) => setF({ ...f, frequency: e.target.value })}
                className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none">
          {taxonomies?.control_frequencies.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </Field>
    </div>
    <Field label="Framework reference (e.g. ISO 27001 A.5.1)">
      <input value={f.framework_ref} onChange={(e) => setF({ ...f, framework_ref: e.target.value })}
             className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none" />
    </Field>
    <Field label={`Mapped risks · ${f.linked_risks.length} selected`}>
      <div className="max-h-40 overflow-y-auto border border-white/10 p-2 space-y-1">
        {risks.length === 0 ? <div className="text-[12px] text-slate-500">No risks to link yet.</div>
          : risks.map((r) => (
            <label key={r.id} className="flex items-center gap-2 p-1.5 cursor-pointer hover:bg-white/5">
              <input type="checkbox" checked={f.linked_risks.includes(r.id)} onChange={() => toggleRisk(r.id)}
                     data-testid={`link-risk-${r.id}`} className="accent-[#D4AF37]" />
              <span className="text-sm text-slate-200 flex-1 truncate">{r.title}</span>
              <span className="font-mono text-[9px] tracking-widest" style={{ color: TIER_COLOR[r.residual_tier] }}>
                {r.residual_score}
              </span>
            </label>
          ))}
      </div>
    </Field>
    <SaveRow onSave={save} onClose={onClose} busy={busy} testid="control-form-save" />
  </Modal>;
}

function ControlTestForm({ control, onClose, onSaved }) {
  const [result, setResult] = useState("effective");
  const [notes, setNotes] = useState("");
  const [evidence, setEvidence] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/grc/controls/${control.id}/test`, { result, notes, evidence_url: evidence });
      toast.success("Test recorded");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };
  return <Modal title={`Test: ${control.title}`} onClose={onClose} testid="control-test-form">
    <Field label="Result">
      <div className="grid grid-cols-3 gap-2">
        {[{ id: "effective", color: "#22C55E" }, { id: "partial", color: "#F59E0B" }, { id: "ineffective", color: "#EF4444" }].map((r) => (
          <button key={r.id} onClick={() => setResult(r.id)} data-testid={`test-result-${r.id}`}
                  className={`py-3 font-mono text-[10px] tracking-widest border ${result === r.id ? "" : "opacity-50"}`}
                  style={{ borderColor: r.color, color: r.color,
                           background: result === r.id ? `${r.color}1A` : "transparent" }}>
            {r.id.toUpperCase()}
          </button>
        ))}
      </div>
    </Field>
    <Field label="Notes"><textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
             className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none" /></Field>
    <Field label="Evidence link (optional)">
      <input value={evidence} onChange={(e) => setEvidence(e.target.value)} placeholder="https://… or vault://doc-id"
             className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none" />
    </Field>
    <SaveRow onSave={save} onClose={onClose} busy={busy} label="RECORD TEST" testid="control-test-save" />
  </Modal>;
}

function ControlHistory({ control, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/grc/controls/${control.id}/tests`).then((r) => setRows(r.data)).finally(() => setLoading(false));
  }, [control.id]);
  return <Modal title={`Test history: ${control.title}`} onClose={onClose} testid="control-history" wide>
    {loading ? <Loading /> : rows.length === 0 ? (
      <div className="text-sm text-slate-400 py-6 text-center">No tests recorded.</div>
    ) : (
      <div className="space-y-1.5">
        {rows.map((t) => (
          <div key={t.id} className="nexus-card p-3 flex items-center gap-3" data-testid={`test-row-${t.id}`}>
            <div className="w-1.5 h-1.5 rounded-full shrink-0"
                 style={{ background: t.result === "effective" ? "#22C55E" : t.result === "partial" ? "#F59E0B" : "#EF4444" }} />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white">
                <span className="font-mono text-[10px] tracking-widest mr-2"
                      style={{ color: t.result === "effective" ? "#22C55E" : t.result === "partial" ? "#F59E0B" : "#EF4444" }}>
                  {t.result.toUpperCase()}
                </span>
                by {t.tested_by_name}
              </div>
              {t.notes && <div className="text-[11px] text-slate-400 mt-1">{t.notes}</div>}
            </div>
            <span className="font-mono text-[10px] text-slate-500 shrink-0">{new Date(t.tested_at).toLocaleString()}</span>
          </div>
        ))}
      </div>
    )}
  </Modal>;
}

/* ─────────────────────────── AUDITS ─────────────────────────── */
function AuditsPane({ taxonomies }) {
  const [rows, setRows] = useState([]);
  const [active, setActive] = useState(null);
  const [showForm, setShowForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    const r = await api.get("/grc/audits");
    setRows(r.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);
  const del = async (id) => {
    if (!confirm("Delete this audit and its findings?")) return;
    await api.delete(`/grc/audits/${id}`);
    toast.success("Audit deleted");
    if (active?.id === id) setActive(null);
    load();
  };

  if (loading) return <Loading />;

  return (
    <div className="space-y-4" data-testid="audits-pane">
      <div className="flex items-center justify-between">
        <span className="label-eyebrow">// Audits · {rows.length}</span>
        <button onClick={() => setShowForm("new")} data-testid="audit-add"
                className="text-[11px] font-mono tracking-widest px-4 py-2 flex items-center gap-2"
                style={{ background: "var(--gold-500)", color: "#050B14" }}>
          <Plus className="w-3 h-3" /> NEW AUDIT
        </button>
      </div>
      {rows.length === 0 ? (
        <div className="nexus-card p-8 text-center text-sm text-slate-400">No audits yet.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {rows.map((a) => (
            <div key={a.id} className="nexus-card p-4 cursor-pointer hover:border-[#D4AF37]/40"
                 onClick={() => setActive(a)} data-testid={`audit-${a.id}`}>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <h3 className="font-display text-base text-white flex-1 truncate">{a.title}</h3>
                <span className="font-mono text-[9px] tracking-widest px-1.5 py-0.5"
                      style={{ background: (STATUS_COLOR[a.status] || "#94A3B8") + "20",
                               color: STATUS_COLOR[a.status] || "#94A3B8" }}>
                  {a.status?.toUpperCase()}
                </span>
              </div>
              {a.scope && <p className="text-[12px] text-slate-400 line-clamp-2">{a.scope}</p>}
              <div className="flex gap-4 mt-3 text-[10px] font-mono tracking-widest text-slate-500 flex-wrap">
                {a.auditor && <span>BY: {a.auditor}</span>}
                <span>FINDINGS: {a.findings_total || 0}</span>
                <span className="text-amber-400">OPEN: {a.findings_open || 0}</span>
                {a.findings_critical > 0 && <span className="text-red-400">CRIT: {a.findings_critical}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      <AnimatePresence>
        {showForm && <AuditForm initial={showForm === "new" ? null : showForm} taxonomies={taxonomies}
                                onClose={() => setShowForm(null)} onSaved={() => { setShowForm(null); load(); }} />}
        {active && <AuditDetail audit={active} taxonomies={taxonomies} onClose={() => setActive(null)}
                                onDelete={() => del(active.id)} onChange={load} />}
      </AnimatePresence>
    </div>
  );
}

function AuditForm({ initial, taxonomies, onClose, onSaved }) {
  const isEdit = !!initial;
  const [f, setF] = useState({
    title: initial?.title || "", scope: initial?.scope || "", auditor: initial?.auditor || "",
    status: initial?.status || "planning",
    period_start: initial?.period_start?.slice(0, 10) || "",
    period_end: initial?.period_end?.slice(0, 10) || "",
    target_close: initial?.target_close?.slice(0, 10) || "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.title.trim()) return toast.error("Title required");
    setBusy(true);
    try {
      if (isEdit) await api.put(`/grc/audits/${initial.id}`, f);
      else await api.post("/grc/audits", f);
      toast.success(isEdit ? "Audit updated" : "Audit created");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };
  return <Modal title={isEdit ? "Edit audit" : "New audit"} onClose={onClose} testid="audit-form">
    <Field label="Title">
      <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="audit-form-title"
             className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none" />
    </Field>
    <Field label="Scope"><textarea rows={2} value={f.scope} onChange={(e) => setF({ ...f, scope: e.target.value })}
             className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none" /></Field>
    <div className="grid grid-cols-2 gap-3">
      <Field label="Auditor"><input value={f.auditor} onChange={(e) => setF({ ...f, auditor: e.target.value })}
             className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none" /></Field>
      <Field label="Status">
        <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}
                className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none">
          {taxonomies?.audit_statuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </Field>
    </div>
    <div className="grid grid-cols-3 gap-3">
      <Field label="Period start"><input type="date" value={f.period_start} onChange={(e) => setF({ ...f, period_start: e.target.value })}
             className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none" /></Field>
      <Field label="Period end"><input type="date" value={f.period_end} onChange={(e) => setF({ ...f, period_end: e.target.value })}
             className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none" /></Field>
      <Field label="Target close"><input type="date" value={f.target_close} onChange={(e) => setF({ ...f, target_close: e.target.value })}
             className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none" /></Field>
    </div>
    <SaveRow onSave={save} onClose={onClose} busy={busy} testid="audit-form-save" />
  </Modal>;
}

function AuditDetail({ audit, taxonomies, onClose, onDelete, onChange }) {
  const [findings, setFindings] = useState([]);
  const [showForm, setShowForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    const r = await api.get(`/grc/audits/${audit.id}/findings`);
    setFindings(r.data);
    setLoading(false);
    onChange?.();
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [audit.id]);

  const delFinding = async (fid) => {
    if (!confirm("Delete finding?")) return;
    await api.delete(`/grc/findings/${fid}`);
    toast.success("Finding deleted");
    load();
  };

  return <Modal title={audit.title} onClose={onClose} testid="audit-detail" wide>
    <div className="flex gap-2 items-center text-[11px] font-mono tracking-widest text-slate-500 mb-4">
      <span style={{ color: STATUS_COLOR[audit.status] }}>{audit.status?.toUpperCase()}</span>
      {audit.auditor && <><span>·</span><span>BY {audit.auditor}</span></>}
      {audit.period_start && <><span>·</span><span>{new Date(audit.period_start).toLocaleDateString()}—{audit.period_end ? new Date(audit.period_end).toLocaleDateString() : "?"}</span></>}
      <div className="flex-1" />
      <button onClick={onDelete} className="p-1.5 border border-red-500/30 text-red-400 hover:bg-red-500/10">
        <Trash2 className="w-3 h-3" />
      </button>
    </div>
    <div className="flex items-center justify-between mb-3">
      <span className="label-eyebrow">// Findings · {findings.length}</span>
      <button onClick={() => setShowForm("new")} data-testid="finding-add"
              className="text-[10px] font-mono tracking-widest px-3 py-1.5 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/8 flex items-center gap-1.5">
        <Plus className="w-3 h-3" /> ADD FINDING
      </button>
    </div>
    {loading ? <Loading /> : findings.length === 0 ? (
      <div className="text-sm text-slate-500 py-4 text-center">No findings logged.</div>
    ) : (
      <div className="space-y-2">
        {findings.map((f) => (
          <div key={f.id} className="nexus-card p-3" data-testid={`finding-${f.id}`}>
            <div className="flex items-start gap-3">
              <div className="w-1 self-stretch shrink-0" style={{ background: SEV_COLOR[f.severity] }} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display text-sm text-white">{f.title}</span>
                  <span className="font-mono text-[9px] tracking-widest" style={{ color: SEV_COLOR[f.severity] }}>{f.severity?.toUpperCase()}</span>
                  <span className="font-mono text-[9px] tracking-widest" style={{ color: STATUS_COLOR[f.status] }}>· {f.status?.toUpperCase()}</span>
                </div>
                {f.description && <p className="text-[12px] text-slate-400 mt-1 leading-snug">{f.description}</p>}
                {f.recommendation && <p className="text-[12px] text-emerald-300 mt-1 leading-snug"><strong>Recommendation:</strong> {f.recommendation}</p>}
                {f.management_response && <p className="text-[12px] text-amber-300 mt-1 leading-snug"><strong>Mgmt response:</strong> {f.management_response}</p>}
                {f.target_remediation_date && <div className="text-[10px] font-mono tracking-widest text-slate-500 mt-1">TARGET: {new Date(f.target_remediation_date).toLocaleDateString()}</div>}
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <button onClick={() => setShowForm(f)} className="p-1.5 border border-white/10 text-slate-400 hover:border-[#D4AF37]/40 hover:text-[#D4AF37]">
                  <Edit3 className="w-3 h-3" />
                </button>
                <button onClick={() => delFinding(f.id)} className="p-1.5 border border-white/10 text-slate-400 hover:border-red-500/40 hover:text-red-400">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    )}
    <AnimatePresence>
      {showForm && <FindingForm auditId={audit.id} initial={showForm === "new" ? null : showForm}
                                taxonomies={taxonomies} onClose={() => setShowForm(null)}
                                onSaved={() => { setShowForm(null); load(); }} />}
    </AnimatePresence>
  </Modal>;
}

function FindingForm({ auditId, initial, taxonomies, onClose, onSaved }) {
  const isEdit = !!initial;
  const [f, setF] = useState({
    title: initial?.title || "", description: initial?.description || "",
    severity: initial?.severity || "medium", status: initial?.status || "open",
    recommendation: initial?.recommendation || "", management_response: initial?.management_response || "",
    target_remediation_date: initial?.target_remediation_date?.slice(0, 10) || "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.title.trim()) return toast.error("Title required");
    setBusy(true);
    try {
      if (isEdit) await api.put(`/grc/findings/${initial.id}`, f);
      else await api.post(`/grc/audits/${auditId}/findings`, f);
      toast.success(isEdit ? "Finding updated" : "Finding logged");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };
  return <Modal title={isEdit ? "Edit finding" : "Add finding"} onClose={onClose} testid="finding-form">
    <Field label="Title"><input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="finding-form-title"
             className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none" /></Field>
    <Field label="Description"><textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })}
             className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none" /></Field>
    <div className="grid grid-cols-2 gap-3">
      <Field label="Severity">
        <select value={f.severity} onChange={(e) => setF({ ...f, severity: e.target.value })}
                className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none">
          {taxonomies?.finding_severities.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </Field>
      <Field label="Status">
        <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}
                className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none">
          {taxonomies?.finding_statuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </Field>
    </div>
    <Field label="Recommendation"><textarea rows={2} value={f.recommendation} onChange={(e) => setF({ ...f, recommendation: e.target.value })}
             className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none" /></Field>
    <Field label="Management response"><textarea rows={2} value={f.management_response} onChange={(e) => setF({ ...f, management_response: e.target.value })}
             className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none" /></Field>
    <Field label="Target remediation date"><input type="date" value={f.target_remediation_date} onChange={(e) => setF({ ...f, target_remediation_date: e.target.value })}
             className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none" /></Field>
    <SaveRow onSave={save} onClose={onClose} busy={busy} testid="finding-form-save" />
  </Modal>;
}

/* ─────────────────────────── ETHICS ─────────────────────────── */
function EthicsPane({ taxonomies }) {
  const { user } = useAuth();
  const canManage = ["admin", "executive"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  const [confirmation, setConfirmation] = useState(null);

  const load = async () => {
    if (!canManage) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const q = filterStatus ? `?status=${filterStatus}` : "";
    const r = await api.get(`/grc/ethics${q}`);
    setRows(r.data);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterStatus]);

  if (loading) return <Loading />;

  return (
    <div className="space-y-4" data-testid="ethics-pane">
      {/* Confidential intake banner */}
      <div className="nexus-glass-panel p-5 flex items-start gap-4 border-l-2" style={{ borderLeftColor: "var(--gold-500)" }}>
        <Lock className="w-5 h-5 text-[#D4AF37] mt-0.5 shrink-0" strokeWidth={1.5} />
        <div className="flex-1">
          <h3 className="font-display text-base text-white">Confidential Reporting Channel</h3>
          <p className="text-sm text-slate-400 mt-1 max-w-2xl leading-relaxed">
            Submit a report of suspected misconduct, fraud, harassment, conflict of interest, or regulatory breach.
            <span className="text-[#D4AF37]"> Your identity is not attached to the report</span> — only org admins and executives can read submitted cases.
          </p>
          <button onClick={() => setShowForm(true)} data-testid="ethics-file"
                  className="mt-3 text-[11px] font-mono tracking-widest px-4 py-2.5 flex items-center gap-2"
                  style={{ background: "var(--gold-500)", color: "#050B14" }}>
            <EyeOff className="w-3 h-3" /> FILE CONFIDENTIAL REPORT
          </button>
        </div>
      </div>

      {!canManage ? (
        <div className="nexus-card p-6 text-center">
          <Lock className="w-6 h-6 text-slate-600 mx-auto mb-2" strokeWidth={1.5} />
          <p className="text-sm text-slate-400">Case management is visible to admins and executives only.</p>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <span className="label-eyebrow">// Cases · {rows.length}</span>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="ethics-filter"
                    className="bg-[#0A111F] border border-white/10 text-sm text-white px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none">
              <option value="">All statuses</option>
              {taxonomies?.ethics_statuses.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          {rows.length === 0 ? (
            <div className="nexus-card p-8 text-center text-sm text-slate-400">No cases yet.</div>
          ) : (
            <div className="space-y-2">
              {rows.map((e) => (
                <div key={e.id} className="nexus-card p-4 cursor-pointer hover:border-[#D4AF37]/40"
                     onClick={() => setActive(e)} data-testid={`ethics-${e.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center shrink-0"
                         style={{ background: (SEV_COLOR[e.severity] || "#94A3B8") + "1A",
                                  border: `1px solid ${SEV_COLOR[e.severity] || "#94A3B8"}40` }}>
                      <MessageSquareWarning className="w-4 h-4" style={{ color: SEV_COLOR[e.severity] }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-display text-sm text-white">{e.category_label}</span>
                        <span className="font-mono text-[9px] tracking-widest" style={{ color: SEV_COLOR[e.severity] }}>· {e.severity?.toUpperCase()}</span>
                        <span className="font-mono text-[9px] tracking-widest" style={{ color: STATUS_COLOR[e.status] }}>· {e.status?.toUpperCase()}</span>
                      </div>
                      <p className="text-[12px] text-slate-400 line-clamp-2 leading-snug">{e.description}</p>
                      <div className="flex gap-4 mt-2 text-[10px] font-mono tracking-widest text-slate-500 flex-wrap">
                        <span>RECEIVED {new Date(e.received_at).toLocaleString()}</span>
                        {e.case_owner_name && <span>OWNER: {e.case_owner_name}</span>}
                        {e.contact_provided && <span className="text-emerald-400">CONTACT PROVIDED</span>}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-600 mt-1 shrink-0" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <AnimatePresence>
        {showForm && <EthicsForm taxonomies={taxonomies}
                                 onClose={() => setShowForm(false)}
                                 onSaved={(r) => { setShowForm(false); setConfirmation(r); load(); }} />}
        {active && <EthicsDetail report={active} taxonomies={taxonomies}
                                 onClose={() => setActive(null)} onChange={load} />}
        {confirmation && <ConfirmationDialog reportId={confirmation.report_id} onClose={() => setConfirmation(null)} />}
      </AnimatePresence>
    </div>
  );
}

function EthicsForm({ taxonomies, onClose, onSaved }) {
  const [cat, setCat] = useState("fraud");
  const [sev, setSev] = useState("medium");
  const [desc, setDesc] = useState("");
  const [identify, setIdentify] = useState(false);
  const [contact, setContact] = useState("");
  const [subj, setSubj] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (desc.trim().length < 20) return toast.error("Description must be at least 20 characters");
    setBusy(true);
    try {
      const r = await api.post("/grc/ethics/intake", {
        category: cat, severity: sev, description: desc.trim(),
        identify_self: identify, contact: identify ? contact : undefined,
        subject_org: subj,
      });
      onSaved(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit");
    } finally {
      setBusy(false);
    }
  };
  return <Modal title="Confidential intake" onClose={onClose} testid="ethics-intake-form" wide>
    <div className="bg-[#D4AF37]/8 border border-[#D4AF37]/30 p-3 text-[12px] text-[#FCE588] leading-snug mb-1">
      <Lock className="w-3 h-3 inline-block mr-1.5 -mt-0.5" />
      Your identity is not recorded on the report. Only admins and executives of your organisation can read submitted cases.
    </div>
    <div className="grid grid-cols-2 gap-3">
      <Field label="Category">
        <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="ethics-cat"
                className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none">
          {taxonomies?.ethics_categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
      </Field>
      <Field label="Severity">
        <select value={sev} onChange={(e) => setSev(e.target.value)} data-testid="ethics-sev"
                className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none">
          {taxonomies?.finding_severities.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </Field>
    </div>
    <Field label="What happened? (be specific, factual, anonymised if needed) · min 20 chars">
      <textarea rows={6} value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="ethics-desc"
                className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none" />
      <div className="text-[10px] text-slate-500 mt-1 font-mono">{desc.length} chars</div>
    </Field>
    <Field label="Subject team/dept (optional)">
      <input value={subj} onChange={(e) => setSubj(e.target.value)}
             className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none" />
    </Field>
    <label className="flex items-start gap-2 cursor-pointer">
      <input type="checkbox" checked={identify} onChange={(e) => setIdentify(e.target.checked)} data-testid="ethics-identify"
             className="accent-[#D4AF37] mt-1" />
      <span className="text-[12px] text-slate-300">
        I want to provide my contact information for follow-up (this is voluntary; the report itself stays unattributed).
      </span>
    </label>
    {identify && (
      <Field label="Contact (email or phone)">
        <input value={contact} onChange={(e) => setContact(e.target.value)}
               className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none" />
      </Field>
    )}
    <SaveRow onSave={submit} onClose={onClose} busy={busy} label="SUBMIT CONFIDENTIALLY" testid="ethics-submit" />
  </Modal>;
}

function ConfirmationDialog({ reportId, onClose }) {
  return <Modal title="Report received" onClose={onClose} testid="ethics-confirm">
    <div className="text-center py-4">
      <Check className="w-10 h-10 text-emerald-400 mx-auto mb-3" strokeWidth={1.5} />
      <p className="text-sm text-slate-200">Your report has been received and will be triaged.</p>
      <p className="text-[11px] text-slate-500 mt-3 font-mono">REFERENCE</p>
      <p className="font-mono text-[#D4AF37] text-sm mt-1">{reportId}</p>
      <p className="text-[11px] text-slate-500 mt-3 leading-relaxed max-w-sm mx-auto">
        Keep this reference number to ask about case progress through a trusted intermediary if needed.
      </p>
    </div>
    <SaveRow onSave={onClose} onClose={onClose} label="ACKNOWLEDGED" hideCancel testid="ethics-confirm-close" />
  </Modal>;
}

function EthicsDetail({ report, taxonomies, onClose, onChange }) {
  const [status, setStatus] = useState(report.status);
  const [notes, setNotes] = useState(report.triage_notes || "");
  const [sev, setSev] = useState(report.severity);
  const [busy, setBusy] = useState(false);
  const save = async (assign = false) => {
    setBusy(true);
    try {
      const r = await api.put(`/grc/ethics/${report.id}`, {
        status, triage_notes: notes, severity: sev, assign_to_me: assign,
      });
      toast.success("Case updated");
      Object.assign(report, r.data);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };
  return <Modal title={`Case ${report.id.slice(0, 8)}`} onClose={onClose} testid="ethics-detail" wide>
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap text-[11px] font-mono tracking-widest">
        <span className="px-2 py-1 border border-white/10 text-slate-300">{report.category_label}</span>
        <span className="px-2 py-1 border" style={{ borderColor: SEV_COLOR[report.severity], color: SEV_COLOR[report.severity] }}>
          {report.severity?.toUpperCase()}
        </span>
        <span className="text-slate-500">RECEIVED {new Date(report.received_at).toLocaleString()}</span>
      </div>
      <div className="nexus-card p-3">
        <div className="label-eyebrow mb-1">// Description</div>
        <p className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">{report.description}</p>
      </div>
      {report.subject_org && (
        <div className="text-[12px] text-slate-400"><strong>Subject:</strong> {report.subject_org}</div>
      )}
      {report.contact_provided && (
        <div className="nexus-card p-3 border-l-2" style={{ borderLeftColor: "var(--gold-500)" }}>
          <div className="label-eyebrow mb-1 text-[#D4AF37]">// Reporter contact provided</div>
          <p className="text-sm text-slate-200">{report.contact || "(reporter has consented; contact stored separately)"}</p>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3 pt-2">
        <Field label="Status">
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="ethics-status"
                  className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none">
            {taxonomies?.ethics_statuses.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Severity">
          <select value={sev} onChange={(e) => setSev(e.target.value)}
                  className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none">
            {taxonomies?.finding_severities.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Triage notes (visible to admins / executives only)">
        <textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="ethics-notes"
                  className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none" />
      </Field>
      <div className="flex gap-2">
        {!report.case_owner_id && (
          <button onClick={() => save(true)} disabled={busy} data-testid="ethics-assign-me"
                  className="text-[10px] font-mono tracking-widest px-3 py-2 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10">
            ASSIGN TO ME
          </button>
        )}
        <div className="flex-1" />
        <button onClick={onClose} className="text-[10px] font-mono tracking-widest px-3 py-2 border border-white/10 text-slate-300 hover:border-white/30">CLOSE</button>
        <button onClick={() => save(false)} disabled={busy} data-testid="ethics-save"
                className="text-[10px] font-mono tracking-widest px-4 py-2" style={{ background: "var(--gold-500)", color: "#050B14" }}>
          {busy ? "SAVING…" : "SAVE"}
        </button>
      </div>
    </div>
  </Modal>;
}

/* ─────────────────────────── Shared primitives ─────────────────────────── */
const Loading = () => (
  <div className="p-10 text-slate-400 font-mono text-xs flex items-center gap-2">
    <Loader2 className="w-3 h-3 animate-spin" /> LOADING…
  </div>
);

const BigStat = ({ label, value, sub, accent, onClick }) => (
  <button onClick={onClick} className="nexus-glass-panel p-5 text-left hover:border-[#D4AF37]/40 transition group">
    <div className="label-eyebrow mb-2">{label}</div>
    <div className="font-display text-4xl text-white">{value}</div>
    {sub && <div className="text-[11px] text-slate-500 mt-1 font-mono tracking-widest">{sub}</div>}
    <div className="h-0.5 mt-3 group-hover:opacity-100 opacity-50 transition" style={{ background: accent }} />
  </button>
);

const Field = ({ label, children }) => (
  <div className="mb-3">
    <label className="label-eyebrow block mb-1.5">{label}</label>
    {children}
  </div>
);

const SaveRow = ({ onSave, onClose, busy, label = "SAVE", testid, hideCancel }) => (
  <div className="flex gap-2 mt-4">
    {!hideCancel && (
      <button onClick={onClose} className="flex-1 text-[10px] font-mono tracking-widest py-2 border border-white/10 text-slate-300 hover:border-white/30">
        CANCEL
      </button>
    )}
    <button onClick={onSave} disabled={busy} data-testid={testid}
            className="flex-1 text-[10px] font-mono tracking-widest py-2 disabled:opacity-50"
            style={{ background: "var(--gold-500)", color: "#050B14" }}>
      {busy ? "SAVING…" : label}
    </button>
  </div>
);

const Modal = ({ title, onClose, children, testid, wide }) => (
  <motion.div
    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    className="fixed inset-0 z-50 flex items-center justify-center p-4"
    style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px)" }}
    onClick={onClose}
    data-testid={testid}
  >
    <motion.div
      initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.96, opacity: 0 }}
      className={`nexus-glass-panel border border-white/10 p-6 w-full ${wide ? "max-w-2xl" : "max-w-md"} max-h-[90vh] overflow-y-auto`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-xl text-white">{title}</h2>
        <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
      </div>
      {children}
    </motion.div>
  </motion.div>
);
